// Third party imports
import axios from "axios";
import { jwtDecode } from "jwt-decode";

// Relative imports
import { refreshAccessToken } from "./authentication.jsx";

/**
 * API client configuration and utilities
 */
// Function to get the subdomain
export const getSubdomain = () => {
  const hostname = window.location.hostname;
  // In development environment on localhost, get from localStorage
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return JSON.parse(localStorage.getItem("subdomain")) || "tnt";
  }
  // In production, the subdomain is the first part of the hostname
  return hostname.split(".")[0];
};
// Function to build the complete API URL with subdomain
export const buildApiUrl = (version = "V0") => {
  const subdomain = getSubdomain();

  const baseApiUrl =
    version === "V1"
      ? import.meta.env.VITE_API_COMPLETE_URL_V1 || "http://localhost:8080/api/V1.0.0/"
      : import.meta.env.VITE_API_COMPLETE_URL || "http://localhost:8080/api/V0.0.0/";

  // If we're on localhost, add subdomain to localhost
  if (baseApiUrl.includes("localhost")) {
    return baseApiUrl.replace("localhost", `${subdomain}.localhost`);
  }

  // For production/staging, add subdomain to the domain
  if (baseApiUrl.includes("://")) {
    return baseApiUrl.replace("://", `://${subdomain}.`);
  }

  return baseApiUrl;
};

// Base API URLs with environment variable fallbacks
export const API_URLS = {
  V0: buildApiUrl("V0"),
  V1: buildApiUrl("V1"),
};

// Default headers configuration
const DEFAULT_HEADERS = {
  accept: "application/json",
  "Content-Type": "application/json",
};

// Helper function to create an axios instance
const createAxiosInstance = (baseURL, needsAuth = true) => {
  const instance = axios.create({
    baseURL,
    headers: DEFAULT_HEADERS,
  });
  if (needsAuth) {
    // Add request interceptor for authentication
    instance.interceptors.request.use(
      (config) => {
        // Add Authorization header if token exists
        const accessToken = JSON.parse(localStorage.getItem("access"));
        if (accessToken) {
          config.headers["Authorization"] = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    // Add response interceptor for token refresh.
    // Per-request opt-outs (axios config keys, e.g. used by api/ai.jsx):
    // - skipRetryOn5xx: a 500 is returned as is, without refreshing the token and repeating the call
    //   (the 401 refresh still applies);
    // - skipAuthRedirect: a 403 is returned as is, without clearing the session and going to /403.
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;
        // Handle 401/500 errors with token refresh
        if (
          (status === 401 || (status === 500 && !originalRequest?.skipRetryOn5xx)) &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;
          // Get refresh token
          const refreshToken = JSON.parse(localStorage.getItem("refresh"));
          // Handle expired or missing refresh token
          if (!refreshToken) {
            return handleSessionExpiration(error);
          }
          try {
            const refreshTokenDecoded = jwtDecode(refreshToken);
            // Check if refresh token is expired
            if (new Date(refreshTokenDecoded.exp * 1000) < new Date()) {
              return handleSessionExpiration(error);
            }
            // Attempt to refresh the token
            const response = await refreshAccessToken(refreshToken);
            const { access: newAccessToken, refresh: newRefreshToken } = response.data;
            // Update tokens in localStorage
            localStorage.setItem("access", JSON.stringify(newAccessToken));
            localStorage.setItem("refresh", JSON.stringify(newRefreshToken));
            // Retry the original request with the new token
            originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            return handleSessionExpiration(refreshError);
          }
        } else if (status === 403 && !originalRequest?.skipAuthRedirect) {
          // Handle forbidden errors
          localStorage.clear();
          clearAiConversations();
          window.location.href = "/403";
          return Promise.reject(error);
        }
        // Handle other errors
        return Promise.reject(error);
      }
    );
  }
  return instance;
};
// Forced logout: the AI assistant conversations (sessionStorage, same prefix as
// components/AiAssistant/aiStore, not imported to avoid an api <-> aiStore cycle) go too
const clearAiConversations = () => {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith("aiAssistant:conversation:"))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Storage blocked: nothing was stored
  }
};
// Helper function to handle session expiration
const handleSessionExpiration = (error) => {
  localStorage.clear();
  clearAiConversations();
  localStorage.setItem("sessionExpired", "true");
  window.location.href = "/login";
  return Promise.reject(error);
};
// Create API client instances
export const loginAxiosInstance = createAxiosInstance(API_URLS.V0, false);
export const axiosInstance = createAxiosInstance(API_URLS.V0);
export const axiosInstanceV1 = createAxiosInstance(API_URLS.V1);
export const axiosInstancePublic = createAxiosInstance(API_URLS.V0, false);

// Export default headers config
export const config = { headers: DEFAULT_HEADERS };
/**
 * Query parameter utilities
 */
// Build query parameter string
export const buildQueryParams = (params) => {
  return Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join("&");
};
// Add query parameters to endpoint
export const addQueryParams = (endpoint = "", queryParams = {}) => {
  const queryString = buildQueryParams(queryParams);
  if (!queryString) return endpoint;
  // Add query parameters with ? or & depending on whether endpoint already has parameters
  const separator = endpoint.includes("?") ? "&" : "?";
  return `${endpoint}${separator}${queryString}`;
};
